import { useAppSelector } from "hooks/utilsHooks/storeHooks";
import { selectDoorState, selectHasDoorBeenOpened } from "slices/doorSlice";
import { selectActiveLocalTransaction, selectCurrentOrderStep } from "slices/transactionSlice";
import { DoorState, type Maybe, TransactionState } from "types";

/**
 * Drawer stage of the non-RFID prototype flow, derived from the regular step machine plus the door.
 * The machine never leaves step 3 on a door close without inventory frames, so the door state decides
 * between "take" and the handoff; a FINISHED frame (step 5) keeps the handoff unless the door was never
 * opened, in which case there is nothing to hand off and the Terminal goes home.
 */
export type NonRfidStage = "welcome" | "open" | "take" | "finish" | "reset";

export const getNonRfidStage = (
	step: number,
	doors: Maybe<DoorState> | undefined,
	transactionState: Maybe<TransactionState> | undefined,
	hasDoorBeenOpened: boolean,
): NonRfidStage => {
	switch (step) {
		case 2:
			return "open";
		case 3:
		case 4:
			return doors === DoorState.Closed || transactionState === TransactionState.StockCheck ? "finish" : "take";
		case 5:
			return hasDoorBeenOpened ? "finish" : "reset";
		default:
			return "welcome";
	}
};

export const useNonRfidStage = (): NonRfidStage => {
	const step = useAppSelector(selectCurrentOrderStep);
	const doors = useAppSelector(selectDoorState);
	const transaction = useAppSelector(selectActiveLocalTransaction);
	const hasDoorBeenOpened = useAppSelector(selectHasDoorBeenOpened);
	return getNonRfidStage(step, doors, transaction?.state, hasDoorBeenOpened);
};
